import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

/* Opt-in "pill" look. `variant="pill"` turns a tab row into clearly clickable
   outlined pill BUTTONS with a single accent fill on the active one. Default
   look is untouched, so rolling this out to another page is one word. */
const LIST_VARIANTS = {
    default: "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
    pill: "flex w-full flex-nowrap items-center justify-start gap-2 overflow-x-auto overflow-y-hidden bg-transparent p-0 pb-1 h-auto text-muted-foreground [scrollbar-width:thin] [-webkit-overflow-scrolling:touch]",
}

const TRIGGER_VARIANTS = {
    default:
        "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
    pill: [
        "group shrink-0 inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full border",
        "min-h-[40px] px-4 py-2 text-sm font-medium ring-offset-background",
        "transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50 active:scale-[0.97]",
        // inactive = neutral outlined pill, AA-readable text
        "data-[state=inactive]:border-slate-300 data-[state=inactive]:bg-white data-[state=inactive]:text-slate-700",
        "dark:data-[state=inactive]:border-slate-600 dark:data-[state=inactive]:bg-slate-900/70 dark:data-[state=inactive]:text-slate-200",
        "data-[state=inactive]:hover:bg-slate-100 data-[state=inactive]:hover:border-slate-400 data-[state=inactive]:hover:text-slate-900",
        "dark:data-[state=inactive]:hover:bg-slate-800 dark:data-[state=inactive]:hover:border-slate-500 dark:data-[state=inactive]:hover:text-white",
        // active = the single accent fill
        "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-sm",
    ].join(" "),
}

const TabsList = React.forwardRef(({ className, variant = "default", ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(LIST_VARIANTS[variant] || LIST_VARIANTS.default, className)}
    {...props} />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef(({ className, variant = "default", ...props }, ref) => {
  const innerRef = React.useRef(null)
  React.useImperativeHandle(ref, () => innerRef.current)

  /* Keep the tapped pill visible by scrolling ITS OWN ROW sideways only.
     Never scrollIntoView (that scrolls the page vertically too) and never on
     mount / re-render — only on an explicit tap. */
  const keepPillInRow = () => {
    const el = innerRef.current
    if (!el || variant !== "pill") return
    const row = el.parentElement
    if (!row || row.scrollWidth <= row.clientWidth) return
    const left = el.offsetLeft
    const right = left + el.offsetWidth
    if (left < row.scrollLeft) row.scrollLeft = Math.max(0, left - 8)
    else if (right > row.scrollLeft + row.clientWidth) row.scrollLeft = right - row.clientWidth + 8
  }

  return (
    <TabsPrimitive.Trigger
      ref={innerRef}
      onClick={(e) => { props.onClick?.(e); keepPillInRow() }}
      className={cn(TRIGGER_VARIANTS[variant] || TRIGGER_VARIANTS.default, className)}
      {...props} />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

/* Small count badge that lives INSIDE a pill trigger. Zero renders muted
   rather than hidden, so the row never changes width as data arrives. */
const TabsCount = ({ value, className, ...props }) => {
  const zero = !Number(value)
  return (
    <span
      className={cn(
        "inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums leading-none",
        zero
          ? "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
          : "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-100",
        "group-data-[state=active]:bg-white/25 group-data-[state=active]:text-primary-foreground",
        className
      )}
      {...props}>
      {Number(value) || 0}
    </span>
  )
}

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props} />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent, TabsCount }
